/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Gallery from './Gallery/Gallery';
import screenfull from 'screenfull';
import './FullScreenGallery.scss';

/**
 * This is the FullScreenGallery component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function FullScreenGallery({
  container,
  open,
  close,
  prev,
  next,
  target,
  images,
}) {
  const targetNode = document.getElementById(target);
  const [active, setActive] = useState(false);

  useEffect(() => {
    function onFullScreenchange({ target }) {
      if (target === targetNode) {
        setActive(screenfull.isFullscreen);
      }
    }
    if (screenfull.isEnabled) {
      screenfull.on('change', onFullScreenchange);
      return function cleanup() {
        screenfull.on('off', onFullScreenchange);
      };
    }
  });

  useEffect(() => {
    if (targetNode) {
      targetNode.classList.add('full-screen-gallery-container');
    }

    return function cleanup() {
      if (targetNode) {
        targetNode.classList.remove('full-screen-gallery-container');
      }
    };
  }, [targetNode, target]);

  useEffect(() => {
    if (active) {
      if (targetNode) {
        targetNode.classList.add('active');
      }
      document.documentElement.classList.add('full-screen-gallery-active');
    } else {
      if (targetNode) {
        targetNode.classList.remove('active');
      }
      document.documentElement.classList.remove('full-screen-gallery-active');
    }

    return function cleanup() {
      if (targetNode) {
        targetNode.classList.remove('active');
      }
      document.documentElement.classList.remove('full-screen-gallery-active');
    };
  }, [targetNode, active]);

  useEffect(() => {
    if (container) {
      if (active) {
        container.dispatchEvent(new CustomEvent('active'));
      } else {
        container.dispatchEvent(new CustomEvent('exit'));
      }
    }
  }, [container, active]);

  if (container) {
    container.open = () => {
      if (screenfull.isEnabled) {
        screenfull.request(targetNode);
      } else {
        setActive(true);
      }
    };
  }

  if (!targetNode) {
    return null;
  }

  if (!active) {
    return (
      <button
        className="button yellow"
        onClick={() => {
          if (screenfull.isEnabled) {
            screenfull.request(targetNode);
          } else {
            setActive(true);
          }
        }}
      >
        {open}
      </button>
    );
  }

  return createPortal(
    <Gallery
      close={close}
      prev={prev}
      next={next}
      images={images}
      onExit={() => {
        if (screenfull.isEnabled) {
          screenfull.exit(targetNode);
        } else {
          setActive(false);
        }
      }}
    />,
    targetNode
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
FullScreenGallery.propTypes = {
  container: PropTypes.object,
  open: PropTypes.string,
  close: PropTypes.string,
  prev: PropTypes.string,
  next: PropTypes.string,
  target: PropTypes.string.isRequired,
  images: PropTypes.arrayOf(PropTypes.object).isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
FullScreenGallery.defaultProps = {
  open: 'Open',
  close: 'Close',
  prev: 'Previous',
  next: 'Next',
  images: [],
};

export default FullScreenGallery;
